import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { JointBillTableRowDetailsContainer } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/layouts/JointBillTableRowLayout/components/JointBillTableRowDetailsContainer/JointBillTableRowDetailsContainer";
import { IconButton } from "@components/IconButton/IconButton";
import { ChevronIcon } from "@components/icons";
import { useJointBillErrorsStore } from "@store/jointBillErrors/jointBillErrors";
import { isNullOrUndefined } from "@utils/common";

interface IJointBillTableRowLayoutProps {
  className?: string;
  nameCell?: JSX.Element | string;
  statusCell?: JSX.Element | string | boolean;
  totalCell?: JSX.Element | string;
  expectedAmount?: JSX.Element | null;
  billDetails?: JSX.Element | boolean;
  warnings?: JSX.Element | boolean;
  billDetailsDefaultOpened?: boolean;
  notification?: JSX.Element | null;
  providerBadge?: JSX.Element | null;
  billDetailIds?: number[];
  canBePaid?: boolean;
  detailsOpenButtonTitle?: string;
  detailsCloseButtonTitle?: string;
  fullWidthBorderTop?: boolean;
  mergeCenterCells?: boolean;
  requestedServiceProviders?: JSX.Element | null;
  style?: CSSProperties;
}

export const JointBillTableRowLayout: React.FC<
  IJointBillTableRowLayoutProps
> = ({
  className,
  nameCell,
  statusCell,
  totalCell,
  expectedAmount,
  billDetails,
  warnings,
  notification,
  billDetailsDefaultOpened,
  providerBadge,
  billDetailIds = [],
  detailsOpenButtonTitle,
  detailsCloseButtonTitle,
  fullWidthBorderTop = false,
  mergeCenterCells = false,
  requestedServiceProviders,
  style,
}) => {
  const { t } = useTranslation();
  const { meterReadingErrors } = useJointBillErrorsStore();
  const [isExpandableContentOpen, setIsExpandableContentOpen] = useState(
    billDetailsDefaultOpened ?? false,
  );

  const toggleExpandableContent = () =>
    setIsExpandableContentOpen(!isExpandableContentOpen);

  useEffect(() => {
    const containsErrors = meterReadingErrors.some((field) =>
      billDetailIds?.includes(field.billDetailId),
    );

    if (containsErrors) {
      setIsExpandableContentOpen(true);
    }
  }, [meterReadingErrors, billDetailIds]);

  return (
    <div className="group contents">
      <div
        className={twMerge(
          "col-start-1 col-end-[span_3] grid grid-cols-subgrid items-center md:col-end-[span_4]",
          className,
        )}
        style={style}
      >
        <div
          className={twMerge(
            "col-span-3 h-px w-full group-[&:nth-child(2)]:hidden md:col-span-4 md:px-6",
            fullWidthBorderTop && "md:px-0",
          )}
        >
          <div className="h-full w-full bg-grey-light" />
        </div>
        <div className="hidden items-center md:flex md:pl-6">
          {(!isNullOrUndefined(billDetails) ||
            !isNullOrUndefined(requestedServiceProviders)) && (
            <IconButton
              icon={ChevronIcon}
              onClick={toggleExpandableContent}
              iconClassName={twMerge(
                "h-5 w-5 text-base-black",
                isExpandableContentOpen && "rotate-180",
              )}
              aria-label={
                isExpandableContentOpen ? t("action.close") : t("action.open")
              }
            />
          )}
        </div>
        <div
          className={twMerge(
            "col-start-1 col-end-[span_2] h-full py-3 pl-4 md:col-start-auto md:col-end-auto md:py-4 md:pl-0",
            !isNullOrUndefined(expectedAmount) && "pb-1 md:pb-4",
            mergeCenterCells &&
              "col-end-[span_3] md:col-start-2 md:col-end-[span_3]",
          )}
        >
          {nameCell}
        </div>
        {!mergeCenterCells && (
          <>
            <div className="hidden md:block">{statusCell}</div>
            <div className="ml-auto pr-4 md:pr-6">{totalCell}</div>
          </>
        )}
        {!isNullOrUndefined(expectedAmount) && (
          <div
            className={twMerge(
              "col-span-3 mb-3 pl-4 md:hidden",
              !!providerBadge && "pl-16",
            )}
          >
            {expectedAmount}
          </div>
        )}
        {!isNullOrUndefined(notification) && (
          <div
            className={twMerge(
              "col-span-3 mb-3 px-4 md:col-span-4 md:pl-[60px] md:pr-6",
            )}
          >
            {notification}
          </div>
        )}
        {!isNullOrUndefined(warnings) && (
          <div
            className={twMerge(
              "col-span-3 mb-8 px-4 md:col-span-4 md:pl-[60px] md:pr-6",
            )}
          >
            {warnings}
          </div>
        )}
        {!isNullOrUndefined(requestedServiceProviders) && (
          <div
            className={twMerge(
              "col-span-3 mb-8 px-4 md:col-span-4 md:pl-[60px] md:pr-6",
              !isExpandableContentOpen && "md:hidden",
            )}
          >
            <JointBillTableRowDetailsContainer
              isOpen={isExpandableContentOpen}
              onToggle={toggleExpandableContent}
              openButtonTitle={
                detailsOpenButtonTitle ??
                t("bill.resquested.openBillDetails.label")
              }
              closeButtonTitle={
                detailsCloseButtonTitle ??
                t("bill.resquested.closeBillDetails.label")
              }
              childrenContainerClassName="md:py-4"
            >
              {requestedServiceProviders}
            </JointBillTableRowDetailsContainer>
          </div>
        )}
        {!isNullOrUndefined(billDetails) && (
          <div
            className={twMerge(
              "col-span-3 mb-8 px-4 md:col-span-4 md:pl-[60px] md:pr-6",
              !isExpandableContentOpen && "md:hidden",
            )}
          >
            <JointBillTableRowDetailsContainer
              isOpen={isExpandableContentOpen}
              onToggle={toggleExpandableContent}
              openButtonTitle={
                detailsOpenButtonTitle ??
                t("bill.interactions.openBillDetails.label")
              }
              closeButtonTitle={
                detailsCloseButtonTitle ??
                t("bill.interactions.closeBillDetails.label")
              }
            >
              {billDetails}
            </JointBillTableRowDetailsContainer>
          </div>
        )}
      </div>
    </div>
  );
};
